/**
 * Тонкая типизированная обёртка над window.Telegram.WebApp.
 *
 * Всё общение с Telegram идёт только отсюда — это единственное место, которое
 * знает про глобальный объект, и единственное, что придётся править при смене
 * версии Bot API.
 */

type HapticStyle = "light" | "medium" | "heavy" | "rigid" | "soft";

interface Inset {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

interface TelegramWebApp {
  initData: string;
  initDataUnsafe?: {
    user?: { id: number; first_name?: string; username?: string; photo_url?: string };
    /** Из ссылки t.me/<бот>?startapp=… — «p_<токен>» ведёт на программу по ссылке. */
    start_param?: string;
  };
  colorScheme: "light" | "dark";
  themeParams: Record<string, string>;
  /** Отступы устройства: чёлка, домашний индикатор. Bot API 8.0. */
  safeAreaInset?: Inset;
  /** Отступы под интерфейсом самого Telegram: «Закрыть», «⋯». Bot API 8.0. */
  contentSafeAreaInset?: Inset;
  isFullscreen?: boolean;
  onEvent?(event: string, handler: () => void): void;
  offEvent?(event: string, handler: () => void): void;
  ready(): void;
  expand(): void;
  requestFullscreen?(): void;
  /** Bot API 7.7. Без этого свайп вниз по странице сворачивает мини-апп. */
  disableVerticalSwipes?(): void;
  enableClosingConfirmation?(): void;
  disableClosingConfirmation?(): void;
  /** Bot API 8.0: иконка мини-аппа на экране «Домой» телефона. */
  addToHomeScreen?(): void;
  checkHomeScreenStatus?(callback: (status: string) => void): void;
  isVersionAtLeast?(version: string): boolean;
  /** Открыть ссылку t.me внутри Telegram — например, выбор чата для отправки. */
  openTelegramLink?(url: string): void;
  /** Bot API 8.0: нативный выбор чата для готового сообщения. */
  shareMessage?(preparedMessageId: string, callback?: (sent: boolean) => void): void;
  setHeaderColor?(color: string): void;
  setBackgroundColor?(color: string): void;
  BackButton?: {
    show(): void;
    hide(): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
  };
  HapticFeedback?: {
    impactOccurred(style: HapticStyle): void;
    notificationOccurred(type: "error" | "success" | "warning"): void;
  };
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export const tg = (): TelegramWebApp | undefined => window.Telegram?.WebApp;

/** initData есть только внутри Telegram. Пусто → показываем фолбэк-экран. */
export const initData = (): string => tg()?.initData ?? "";

export const isInsideTelegram = (): boolean => initData().length > 0;

/**
 * Переносит отступы Telegram в CSS-переменные `--safe-top` / `--safe-bottom`.
 *
 * В fullscreen кнопки «Закрыть» и «⋯» рисуются ПОВЕРХ страницы, а
 * `env(safe-area-inset-top)` про них не знает — он про чёлку устройства.
 * Нужную величину даёт `contentSafeAreaInset`; её и складываем с аппаратной.
 */
function applyInsets(app: TelegramWebApp): void {
  const root = document.documentElement;
  const device = app.safeAreaInset;
  const content = app.contentSafeAreaInset;
  if (!device && !content) return;

  const top = (device?.top ?? 0) + (content?.top ?? 0);
  const bottom = (device?.bottom ?? 0) + (content?.bottom ?? 0);
  root.style.setProperty("--safe-top", `${top}px`);
  root.style.setProperty("--safe-bottom", `${bottom}px`);
}

/**
 * Подготовка окна: разворачиваем и считаем безопасные отступы.
 *
 * Fullscreen включаем ТОЛЬКО если клиент умеет отдавать `contentSafeAreaInset`.
 * Без него мы не знаем, где заканчиваются кнопки Telegram, и они лягут на
 * содержимое — так уже было. Подставлять сюда угаданное число нельзя: у разных
 * клиентов и платформ высота разная.
 */
export function setupViewport(): void {
  const app = tg();
  if (!app) return;

  app.ready();
  app.expand();
  // Свайп вниз по умолчанию сворачивает окно. В зале список листают потной
  // рукой, и приложение, закрытое посреди подхода, — худший исход из возможных.
  // На клиентах старше Bot API 7.7 метода нет, там остаётся как было.
  app.disableVerticalSwipes?.();

  if (app.requestFullscreen && app.contentSafeAreaInset) {
    try {
      app.requestFullscreen();
    } catch {
      // Клиент отказал — остаёмся в обычном развёрнутом режиме.
    }
  }

  applyInsets(app);
  // Отступы меняются на повороте, при входе и выходе из fullscreen.
  for (const event of ["safeAreaChanged", "contentSafeAreaChanged", "fullscreenChanged"]) {
    app.onEvent?.(event, () => applyInsets(app));
  }

}

/**
 * Покрасить шапку и фон самого Telegram.
 *
 * Цвет приходит из темы приложения (`lib/theme.ts`): расхождение с `--bg`
 * видно полосой над экраном — на светлой теме особенно.
 */
export function setWindowColor(color: string): void {
  const app = tg();
  app?.setHeaderColor?.(color);
  app?.setBackgroundColor?.(color);
}

/**
 * Спрашивать подтверждение перед закрытием окна, пока идёт тренировка.
 *
 * Подходы при закрытии не теряются — они уже на устройстве, — но таймер отдыха
 * умирает вместе с окном, а открыть заново посреди подхода — лишние секунды.
 */
export function setClosingGuard(on: boolean): void {
  const app = tg();
  if (on) app?.enableClosingConfirmation?.();
  else app?.disableClosingConfirmation?.();
}

/** Подписка на событие окна Telegram. Возвращает отписку. */
export function onTelegramEvent(event: string, handler: () => void): () => void {
  const app = tg();
  app?.onEvent?.(event, handler);
  return () => app?.offEvent?.(event, handler);
}

export function haptic(style: HapticStyle = "light"): void {
  tg()?.HapticFeedback?.impactOccurred(style);
}

/** Отклик на исход действия — отличается от haptic() и по ощущению, и по смыслу. */
export function notify(type: "error" | "success" | "warning"): void {
  tg()?.HapticFeedback?.notificationOccurred(type);
}

/**
 * Нативная кнопка «назад» в шапке Telegram.
 *
 * Своей стрелки в интерфейсе не рисуем: у Telegram она уже есть, и пользователь
 * ищет её именно там. Возвращает функцию отписки — обязательно звать при уходе
 * с экрана, иначе обработчики копятся и назад срабатывает несколько раз.
 */
export function useNativeBack(handler: (() => void) | null): () => void {
  const button = tg()?.BackButton;
  if (!button) return () => {};

  if (!handler) {
    button.hide();
    return () => {};
  }

  button.onClick(handler);
  button.show();
  return () => {
    button.offClick(handler);
    button.hide();
  };
}

export type HomeScreenStatus = "unsupported" | "unknown" | "added" | "missed";

/**
 * Можно ли вынести RACK на экран «Домой» и не вынесен ли уже.
 *
 * Метод есть с Bot API 8.0 и только на телефонах: десктопный Telegram отвечает
 * «unsupported» — тогда карточку не показываем вовсе.
 */
export function homeScreenStatus(): Promise<HomeScreenStatus> {
  const app = tg();
  if (!app?.addToHomeScreen || !app.checkHomeScreenStatus || !app.isVersionAtLeast?.("8.0")) {
    return Promise.resolve("unsupported");
  }
  return new Promise((resolve) => {
    try {
      app.checkHomeScreenStatus!((status) => resolve((status as HomeScreenStatus) || "unknown"));
    } catch {
      resolve("unsupported");
    }
  });
}

/** Показать системный диалог «На экран «Домой»». onAdded — когда человек согласился. */
export function addToHomeScreen(onAdded: () => void): () => void {
  const off = onTelegramEvent("homeScreenAdded", onAdded);
  tg()?.addToHomeScreen?.();
  return off;
}

/** Параметр запуска мини-аппа (startapp). В браузере — из адреса, для отладки. */
export const startParam = (): string | null =>
  tg()?.initDataUnsafe?.start_param ??
  new URLSearchParams(window.location.search).get("tgWebAppStartParam");

/**
 * Открыть ссылку t.me внутри Telegram — например, чат с ботом. Вне Telegram
 * (стенд, браузер) — обычная вкладка.
 */
export function openTelegramLink(url: string): void {
  const app = tg();
  if (app?.openTelegramLink) app.openTelegramLink(url);
  else window.open(url, "_blank");
}

/**
 * Отправить ссылку в чат: системный выбор получателя Telegram.
 * Возвращает false, если клиент так не умеет, — тогда предлагаем скопировать.
 */
export function shareToTelegram(url: string, text: string): boolean {
  const app = tg();
  if (!app?.openTelegramLink) return false;
  app.openTelegramLink(
    `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
  );
  return true;
}

/**
 * Нативный выбор чата для готовой карточки (Bot API 8.0).
 *
 * Лучший путь: человек выбирает получателя прямо здесь, и карточка уходит
 * сама. Старый клиент — возвращаем false, и остаётся пересылка из чата бота.
 */
export function shareMessage(preparedMessageId: string): boolean {
  const app = tg();
  if (!app?.shareMessage || !app.isVersionAtLeast?.("8.0")) return false;
  app.shareMessage(preparedMessageId);
  return true;
}

/**
 * Открыть чат внутри Telegram — например, диалог с ботом, куда ушла карточка.
 * Без этого человек остаётся в приложении и не видит, что отправлять дальше.
 */
export function openTelegramChat(url: string): boolean {
  const app = tg();
  if (!app?.openTelegramLink) return false;
  app.openTelegramLink(url);
  return true;
}
